import { useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { C } from '../../theme/palette';
import { useReglages } from '../../api/reglages.jsx';
import { NOM_LOGICIEL, avecLeNom } from '../../lib/marque';

/**
 * L'adresse à partager : celle où l'application est servie.
 *
 * <p>Le lien proposé par défaut était figé sur un domaine qui n'est pas celui
 * de l'installation : l'invitation envoyée par WhatsApp, SMS ou courriel
 * menait le destinataire sur le site d'un tiers.
 *
 * @returns {string} l'origine de la page courante
 */
export function adresseDeLApplication() {
  return typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
}

export function PartagePage({ onNav, soc, estMixte, univers, setUnivers }) {
  const { reglage, setReglage } = useReglages();
  const [lien, setLien] = useState(() => {
    try {
      return reglage('appUrl', adresseDeLApplication());
    } catch (e) {
      return adresseDeLApplication();
    }
  });
  const [copie, setCopie] = useState(false);
  const texte = avecLeNom(
    "Découvre SwissTech, l'application de gestion pour PME suisses (devis, factures QR, chantiers, stock, paie). Tu peux l'essayer ici : " +
      lien,
  );
  const wa = 'https://wa.me/?text=' + encodeURIComponent(texte);
  const sms = 'sms:?&body=' + encodeURIComponent(texte);
  const mail =
    'mailto:?subject=' +
    encodeURIComponent(avecLeNom('SwissTech — gestion pour PME')) +
    '&body=' +
    encodeURIComponent(texte);
  const copier = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(lien);
        setCopie(true);
        setTimeout(() => setCopie(false), 1800);
      }
    } catch (e) {}
  };
  const partagerNatif = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: NOM_LOGICIEL, text: texte, url: lien });
      } else {
        copier();
      }
    } catch (e) {}
  };
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="📣 Partager l'application"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        style={{
          background: 'linear-gradient(135deg,' + C.navy + ',' + C.navyM + ')',
          borderRadius: 16,
          padding: 20,
          marginBottom: 14,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 34, marginBottom: 6 }}>📣</div>
        <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>
          Faites découvrir SwissTech
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.7)', marginTop: 6, lineHeight: 1.5 }}>
          Partagez l'application à une connaissance. Elle l'essaiera avec <b>son propre compte</b>.
        </div>
      </div>

      <Card style={{ marginBottom: 12, border: '1px solid ' + C.greenL }}>
        <div style={{ padding: 13, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 18 }}>🔒</span>
          <div style={{ fontSize: 9.5, color: C.ink, lineHeight: 1.5 }}>
            <b>Tes données restent privées.</b> Ce lien ne donne <b>aucun accès</b> à ton
            application ni à tes clients, devis ou factures. La personne crée sa propre entreprise,
            séparée de la tienne.
          </div>
        </div>
      </Card>

      <Card style={{ marginBottom: 12 }}>
        <Bar icon="🔗" title="Lien de l'application" color={C.teal} />
        <div style={{ padding: 13 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={lien}
              onChange={(e) => {
                setLien(e.target.value);
                try {
                  setReglage('appUrl', e.target.value);
                } catch (err) {}
              }}
              placeholder="Adresse de ton app hébergée (https://…)"
              style={{
                flex: 1,
                background: C.navyXL,
                borderRadius: 9,
                padding: '11px 12px',
                fontSize: 11,
                fontWeight: 700,
                color: C.navy,
                border: '1px solid ' + C.border,
                minWidth: 0,
              }}
            />
            <button
              onClick={copier}
              style={{
                background: copie ? C.green : C.navy,
                border: 'none',
                color: '#fff',
                borderRadius: 9,
                padding: '11px 14px',
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {copie ? '✓ Copié' : 'Copier'}
            </button>
          </div>
        </div>
      </Card>

      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: C.muted,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        Partager via
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 12 }}>
        <a href={wa} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          <div
            style={{
              background: C.greenL,
              borderRadius: 12,
              padding: '15px',
              textAlign: 'center',
              color: C.green,
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            💬 WhatsApp
          </div>
        </a>
        <a href={mail} style={{ textDecoration: 'none' }}>
          <div
            style={{
              background: C.tealL,
              borderRadius: 12,
              padding: '15px',
              textAlign: 'center',
              color: C.teal,
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            ✉ E-mail
          </div>
        </a>
        <a href={sms} style={{ textDecoration: 'none' }}>
          <div
            style={{
              background: C.navyXL,
              borderRadius: 12,
              padding: '15px',
              textAlign: 'center',
              color: C.navy,
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            💬 SMS
          </div>
        </a>
        <div
          onClick={partagerNatif}
          style={{
            background: C.amber + '18',
            borderRadius: 12,
            padding: '15px',
            textAlign: 'center',
            color: C.amber,
            fontWeight: 800,
            fontSize: 11,
            cursor: 'pointer',
          }}
        >
          📤 Autre…
        </div>
      </div>

      <div style={{ fontSize: 8, color: C.muted, textAlign: 'center', lineHeight: 1.5 }}>
        « Autre… » utilise le partage natif du téléphone (AirDrop, Messenger, etc.) quand il est
        disponible.
      </div>
    </Shell>
  );
}
